import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { writeAdminAuditEvent } from 'lib/adminAudit';
import { getExternalDb } from 'lib/firebase';
import { markStaticSeoRefreshRequired } from 'lib/seoRefreshStatus';
import { requestPublicCourse } from 'lib/siteCoursesApi';

const WEBINAR_COLLECTION = 'webinar_content';
const FEATURED_WEBINAR_ID = 'featured';

export const DEFAULT_FEATURED_WEBINAR_CONTENT = {
  status: 'published',
  eyebrow: "Ten recorded courses",
  title: "Complete Trucking Operations Course Series",
  subject: "For people who handle several parts of a trucking office.",
  dateTime: 'Date and time to be announced',
  description: "Study the current ten recorded trucking operations courses together, from driver records and onboarding to Safety, recruiting, dispatch and management. Learn what recruiting needs to pass to onboarding and how management reviews unfinished work across departments. Buying the series saves $1,491 compared with ten individual courses at $299 each.",
  price: '1499',
  regularPrice: '1999',
  primaryCtaLabel: 'Buy Full Series',
  primaryCtaPath: '/webinars/complete-webinar-series',
  secondaryCtaLabel: 'View Full Course Offer',
  featuredImageUrl: '',
  featuredImageAlt: '',
  shortOverviewVideoUrl: '',
  fullOverviewVideoUrl: '',
  agenda: [
    "Driver file checks & recordkeeping",
    "DOT Audit Document Organization",
    "DataQs & Inspection Record Review",
    "Safety Meetings & Incident Follow-Up",
    "SOPs & Internal Workflows",
    "Driver Onboarding & Refresher Training",
    "Driver recruiting follow-up",
    "TMS Workflows & Dispatch Handoffs",
    "Management Visibility & Open Items Review",
    "Mapping a trucking company process",
  ],
};

function getDbOrThrow() {
  const db = getExternalDb();
  if (!db) {
    throw new Error('Course CMS is not configured. Check Firebase environment variables.');
  }
  return db;
}

function sanitizeText(value) {
  return String(value || '').trim();
}

function splitLines(value) {
  if (Array.isArray(value)) {
    return value.map(sanitizeText).filter(Boolean).slice(0, 12);
  }

  return sanitizeText(value)
    .split(/\n+/)
    .map(sanitizeText)
    .filter(Boolean)
    .slice(0, 12);
}

function normalizeInternalPath(value, fallback) {
  const path = sanitizeText(value);
  return path.startsWith('/') && !path.startsWith('//') ? path : fallback;
}

function normalizeMediaUrl(value) {
  const url = sanitizeText(value);
  if (!url) {
    return '';
  }
  if (url.startsWith('/') && !url.startsWith('//')) {
    return url;
  }

  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:') {
      return parsed.toString();
    }
    if (parsed.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname)) {
      return parsed.toString();
    }
  } catch (error) {
    return '';
  }
  return '';
}

function normalizeImageUrl(value) {
  return normalizeMediaUrl(value);
}

function normalizeContent(data = {}) {
  return {
    ...DEFAULT_FEATURED_WEBINAR_CONTENT,
    status: sanitizeText(data.status) === 'draft' ? 'draft' : 'published',
    eyebrow: sanitizeText(data.eyebrow) || DEFAULT_FEATURED_WEBINAR_CONTENT.eyebrow,
    title: sanitizeText(data.title) || DEFAULT_FEATURED_WEBINAR_CONTENT.title,
    subject: sanitizeText(data.subject) || DEFAULT_FEATURED_WEBINAR_CONTENT.subject,
    dateTime: sanitizeText(data.dateTime) || DEFAULT_FEATURED_WEBINAR_CONTENT.dateTime,
    description: sanitizeText(data.description) || DEFAULT_FEATURED_WEBINAR_CONTENT.description,
    price: sanitizeText(data.price) || DEFAULT_FEATURED_WEBINAR_CONTENT.price,
    regularPrice: sanitizeText(data.regularPrice) || DEFAULT_FEATURED_WEBINAR_CONTENT.regularPrice,
    primaryCtaLabel: sanitizeText(data.primaryCtaLabel) || DEFAULT_FEATURED_WEBINAR_CONTENT.primaryCtaLabel,
    primaryCtaPath: normalizeInternalPath(data.primaryCtaPath, DEFAULT_FEATURED_WEBINAR_CONTENT.primaryCtaPath),
    secondaryCtaLabel: sanitizeText(data.secondaryCtaLabel) || DEFAULT_FEATURED_WEBINAR_CONTENT.secondaryCtaLabel,
    featuredImageUrl: normalizeImageUrl(data.featuredImageUrl),
    featuredImageAlt: sanitizeText(data.featuredImageAlt),
    shortOverviewVideoUrl: normalizeMediaUrl(data.shortOverviewVideoUrl),
    fullOverviewVideoUrl: normalizeMediaUrl(data.fullOverviewVideoUrl),
    agenda: splitLines(data.agenda).length ? splitLines(data.agenda) : DEFAULT_FEATURED_WEBINAR_CONTENT.agenda,
    updatedAt: data.updatedAt || null,
    updatedBy: sanitizeText(data.updatedBy),
  };
}

export function buildEditableFeaturedWebinarContent(content = DEFAULT_FEATURED_WEBINAR_CONTENT) {
  const normalized = normalizeContent(content);
  return {
    ...normalized,
    agenda: normalized.agenda.join('\n'),
  };
}

export async function getPublicFeaturedWebinarContent() {
  try {
    const record = await requestPublicCourse('/featured');
    return record?.status === 'published' ? normalizeContent(record) : DEFAULT_FEATURED_WEBINAR_CONTENT;
  } catch {
    return DEFAULT_FEATURED_WEBINAR_CONTENT;
  }
}

export async function getAdminFeaturedWebinarContent() {
  const db = getDbOrThrow();
  const snapshot = await getDoc(doc(db, WEBINAR_COLLECTION, FEATURED_WEBINAR_ID));
  return buildEditableFeaturedWebinarContent(snapshot.exists() ? snapshot.data() : DEFAULT_FEATURED_WEBINAR_CONTENT);
}

export async function saveFeaturedWebinarContent({ content, adminEmail = '' }) {
  const db = getDbOrThrow();
  const saved = await getDoc(doc(db, WEBINAR_COLLECTION, FEATURED_WEBINAR_ID));
  if (['featuredImageUrl', 'featuredImageAlt'].some(field => (content[field] || '') !== (saved.data()?.[field] || '')))
    throw new Error('Edit the course promotion poster in Site content’s page media controls.');
  const title = sanitizeText(content.title);
  const description = sanitizeText(content.description);

  if (!title) {
    throw new Error('Course title is required.');
  }

  if (!description) {
    throw new Error('Course description is required.');
  }

  const payload = {
    status: sanitizeText(content.status) === 'draft' ? 'draft' : 'published',
    eyebrow: sanitizeText(content.eyebrow) || DEFAULT_FEATURED_WEBINAR_CONTENT.eyebrow,
    title,
    subject: sanitizeText(content.subject),
    dateTime: sanitizeText(content.dateTime) || DEFAULT_FEATURED_WEBINAR_CONTENT.dateTime,
    description,
    price: sanitizeText(content.price) || DEFAULT_FEATURED_WEBINAR_CONTENT.price,
    regularPrice: sanitizeText(content.regularPrice) || DEFAULT_FEATURED_WEBINAR_CONTENT.regularPrice,
    primaryCtaLabel: sanitizeText(content.primaryCtaLabel) || DEFAULT_FEATURED_WEBINAR_CONTENT.primaryCtaLabel,
    primaryCtaPath: normalizeInternalPath(content.primaryCtaPath, DEFAULT_FEATURED_WEBINAR_CONTENT.primaryCtaPath),
    secondaryCtaLabel: sanitizeText(content.secondaryCtaLabel) || DEFAULT_FEATURED_WEBINAR_CONTENT.secondaryCtaLabel,
    featuredImageUrl: normalizeImageUrl(content.featuredImageUrl),
    featuredImageAlt: sanitizeText(content.featuredImageAlt),
    shortOverviewVideoUrl: normalizeMediaUrl(content.shortOverviewVideoUrl),
    fullOverviewVideoUrl: normalizeMediaUrl(content.fullOverviewVideoUrl),
    agenda: splitLines(content.agenda),
    updatedAt: serverTimestamp(),
    updatedBy: sanitizeText(adminEmail),
  };

  await setDoc(doc(db, WEBINAR_COLLECTION, FEATURED_WEBINAR_ID), payload, { merge: true });
  await writeAdminAuditEvent({
    action: payload.status === 'published' ? 'published' : 'saved-draft',
    targetCollection: WEBINAR_COLLECTION,
    targetId: FEATURED_WEBINAR_ID,
    summary: 'Featured course content saved',
    adminEmail,
    metadata: {
      status: payload.status,
      title: payload.title,
      price: payload.price,
      hasShortVideo: Boolean(payload.shortOverviewVideoUrl),
      hasFullVideo: Boolean(payload.fullOverviewVideoUrl),
    },
  });
  if (payload.status === 'published') {
    await markStaticSeoRefreshRequired({
      action: 'updated',
      source: 'webinar',
      path: '/webinars',
      title: payload.title,
      adminEmail,
    }).catch((error) => {
      console.error('Failed to mark static SEO refresh required after course update:', error);
    });
  }
  return payload;
}
